import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { RootState } from '@/App/store'
import { useKeyboardNavigation } from '@/features/StoreMapView/hooks/useKeyboardNavigation'
import { useStoreDistances } from '@/hooks/useStoreDistances'
import { ScreenHeader } from '@/features/AddressSearchScreen/Components/ScreenHeader'
import { InteractiveMap } from './Components/InteractiveMap'
import { MobileStoreCard } from './Components/MobileStoreCard'
import { DesktopStoreCard } from './Components/DesktopViewStoreCards'
import type { Store } from '@/types'

export function MapViewScreen() {
  const navigate = useNavigate()
  const storeData = useSelector((state: RootState) => state.store.store)

  const rawStores = useMemo(() => {
    if (!storeData?.stores) return []
    return storeData.stores
  }, [storeData])

  const { stores: sortedPickupStores } = useStoreDistances(rawStores)

  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(
    sortedPickupStores[0]?.storeId || null
  )
  const carouselRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [isDesktop, setIsDesktop] = useState(false)
  const CARD_WIDTH = 280
  const CARD_GAP = 12
  const CARD_TOTAL_WIDTH = CARD_WIDTH + CARD_GAP

  // Check for desktop viewport
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024)
    checkDesktop()
    window.addEventListener('resize', checkDesktop)
    return () => window.removeEventListener('resize', checkDesktop)
  }, [])


  const scrollToCard = useCallback((storeId: string) => {
    if (!carouselRef.current) return
    const index = sortedPickupStores.findIndex((s: Store) => s.storeId === storeId)
    if (index === -1) return

    carouselRef.current.scrollTo({
      left: index * CARD_TOTAL_WIDTH,
      behavior: 'smooth',
    })
  }, [sortedPickupStores, CARD_TOTAL_WIDTH])


  const scrollToListItem = useCallback((storeId: string) => {
    if (!listRef.current) return
    const element = listRef.current.querySelector(`[data-store-id="${storeId}"]`)
    element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])


  const triggerHaptic = useCallback((duration: number) => {
    if (navigator.vibrate) {
      navigator.vibrate(duration)
    }
  }, [])


  const selectStore = useCallback((storeId: string) => {
    setSelectedStoreId(storeId)
    triggerHaptic(15)
  }, [triggerHaptic])


  const onStoreSelect = useCallback((storeId: string) => {
    setSelectedStoreId(storeId)

    if (isDesktop) {
      scrollToListItem(storeId)
    } else {
      scrollToCard(storeId)
    }

    triggerHaptic(10)
  }, [isDesktop, scrollToListItem, scrollToCard, triggerHaptic])


  const handleCardClick = useCallback((store: Store) => {
    selectStore(store.storeId)
    if (store.baseUrl) {
      window.open(store.baseUrl, '_blank', 'noopener,noreferrer')
    }
    triggerHaptic(15)
  }, [selectStore, triggerHaptic])


  // Sync carousel scroll with selected store (mobile only)
  useEffect(() => {
    if (isDesktop) return
    const carousel = carouselRef.current
    if (!carousel) return

    const handleScroll = () => {
      const scrollLeft = carousel.scrollLeft
      const index = Math.round(scrollLeft / CARD_TOTAL_WIDTH)
      const store = sortedPickupStores[index]

      if (store && store.storeId !== selectedStoreId) {
        setSelectedStoreId(store.storeId)
      }
    }

    carousel.addEventListener('scroll', handleScroll, { passive: true })
    return () => carousel.removeEventListener('scroll', handleScroll)
  }, [sortedPickupStores, selectedStoreId, isDesktop, CARD_TOTAL_WIDTH])

  const getCurrentStoreIndex = useCallback(() => {
    return sortedPickupStores.findIndex((s: Store) => s.storeId === selectedStoreId)
  }, [sortedPickupStores, selectedStoreId])

  const handleNext = useCallback(() => {
    const currentIndex = getCurrentStoreIndex()
    if (currentIndex === -1) return

    const nextIndex = Math.min(currentIndex + 1, sortedPickupStores.length - 1)
    const nextStore = sortedPickupStores[nextIndex]
    if (nextStore && nextStore.storeId !== selectedStoreId) {
      onStoreSelect(nextStore.storeId)
    }
  }, [getCurrentStoreIndex, sortedPickupStores, selectedStoreId, onStoreSelect])

  const handlePrevious = useCallback(() => {
    const currentIndex = getCurrentStoreIndex()
    if (currentIndex === -1) return

    const prevIndex = Math.max(currentIndex - 1, 0)
    const prevStore = sortedPickupStores[prevIndex]
    if (prevStore && prevStore.storeId !== selectedStoreId) {
      onStoreSelect(prevStore.storeId)
    }
  }, [getCurrentStoreIndex, sortedPickupStores, selectedStoreId, onStoreSelect])

  const handleConfirm = useCallback(() => {
    const store = sortedPickupStores.find((s: Store) => s.storeId === selectedStoreId)
    if (store) handleCardClick(store)
  }, [sortedPickupStores, selectedStoreId, handleCardClick])

  const handleEscape = useCallback(() => {
    navigate(-1)
  }, [navigate])

  useKeyboardNavigation({
    onNext: handleNext,
    onPrevious: handlePrevious,
    onConfirm: handleConfirm,
    onEscape: handleEscape,
  })

  return (
    <div className="min-h-dvh flex flex-col bg-surface-secondary">
      <ScreenHeader
        title="Select Store"
        showBack
        onBack={() => navigate(-1)}
      />

      <div className="flex-1 flex flex-col lg:flex-row">
        <div className="flex-1 relative min-h-[50vh] lg:min-h-0">
          <div className="absolute inset-0">
            <InteractiveMap
              stores={sortedPickupStores}
              selectedId={selectedStoreId}
              onSelectStore={onStoreSelect}
            />
          </div>

          <div className="lg:hidden absolute inset-x-0 bottom-0 pointer-events-none">
            <div
              ref={carouselRef}
              className="flex gap-3 px-4 pt-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide touch-pan-x pointer-events-auto focus-within:outline-none"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
              }}
              role="listbox"
              aria-label="Store locations"
              aria-activedescendant={selectedStoreId || undefined}
            >
              {sortedPickupStores.map((store: Store) => (
                <MobileStoreCard
                  key={store.storeId}
                  store={store}
                  isSelected={(store.storeId) === selectedStoreId}
                  onSelect={() => handleCardClick(store)}
                  onFocus={() => setSelectedStoreId(store.storeId)}
                />
              ))}
            </div>
          </div>
        </div>

        <aside className="hidden lg:flex flex-col w-[400px] border-l border-border-light bg-surface-primary">
          <div className="p-4 border-b border-border-light">
            <h2 className="font-semibold text-text-primary">
              {sortedPickupStores.length} stores nearby
            </h2>
          </div>

          <div
            ref={listRef}
            className="flex-1 overflow-y-auto focus-within:outline-none"
            role="listbox"
            aria-label="Store locations"
            aria-activedescendant={selectedStoreId || undefined}
          >
            {sortedPickupStores.map((store: Store) => (
              <DesktopStoreCard
                key={store.storeId}
                store={store}
                isSelected={(store.storeId) === selectedStoreId}
                onSelect={() => handleCardClick(store)}
                onHover={() => setSelectedStoreId(store.storeId)}
              />
            ))}
          </div>
        </aside>
      </div>

      <div className="sr-only" role="status" aria-live="polite">
        {selectedStoreId && `Selected ${sortedPickupStores.find((s: Store) => (s.storeId) === selectedStoreId)?.name}`}
      </div>
    </div>
  )
}
